import { useMemo } from 'react';
import {
  Award,
  Copy,
  Crown,
  Dices,
  Download,
  FileSpreadsheet,
  Lock,
  RotateCcw,
  Scale,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { UserCell } from '@/components/tombolo/UserCell';
import { useToast } from '@/hooks/useToast';
import {
  actionLabel,
  buildFullReport,
  buildSummaryText,
  displayName,
  downloadTextFile,
  filteredOutToCsv,
  formatTimestamp,
  ticketsToCsv,
  type SummaryData,
} from '@/lib/tombolo';
import type { TomboloState } from '@/hooks/useTombolo';

export function StepDraw({ state }: { state: TomboloState }) {
  const {
    finalized, engagement, drawnInput, setDrawnInput, runDraw, outcome, drawAt,
    settings, resetAll,
  } = state;
  const { toast } = useToast();

  const summary: SummaryData | null = useMemo(() => {
    if (!finalized || !engagement || !outcome || !drawAt) return null;
    return {
      notesScanned: engagement.notesScanned,
      eligibleTickets: finalized.tickets.length,
      drawnNumber: outcome.drawnNumber,
      winnerName: `${displayName(outcome.winner.participant.pubkey, engagement.profiles[outcome.winner.participant.pubkey])} (${outcome.winner.participant.npub})`,
      winnerTicket: outcome.winner.number,
      runnerUpName: outcome.runnerUp
        ? `${displayName(outcome.runnerUp.participant.pubkey, engagement.profiles[outcome.runnerUp.participant.pubkey])} (${outcome.runnerUp.participant.npub})`
        : undefined,
      runnerUpTicket: outcome.runnerUp?.number,
      finalizedAt: finalized.finalizedAt,
      drawAt,
    };
  }, [finalized, engagement, outcome, drawAt]);

  if (!finalized || !engagement) return null;

  const maxTicket = finalized.tickets.length;
  const drawnNumber = Number(drawnInput.trim());
  const isEntered = drawnInput.trim() !== '';
  const inputValid = isEntered
    && Number.isInteger(drawnNumber)
    && drawnNumber >= 1
    && drawnNumber <= maxTicket;

  const copyText = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${what} copied to clipboard` });
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Finalization banner */}
      <Card className="border-emerald-600/40 bg-emerald-50 dark:bg-emerald-950/30">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-emerald-700 dark:text-emerald-400 mt-0.5 shrink-0" aria-hidden />
            <div className="text-sm">
              <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                Ticket list is locked — the raffle is ready for the draw.
              </p>
              <p className="text-emerald-700 dark:text-emerald-400">
                {maxTicket} tickets finalized at {formatTimestamp(finalized.finalizedAt)}.
                Tickets, participants, filter settings and note IDs can no longer change.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pl-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTextFile(
                'nostr-tombolo-tickets.csv',
                ticketsToCsv(finalized.tickets, engagement.profiles),
                'text/csv',
              )}
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" aria-hidden />
              Ticket list (CSV)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTextFile(
                'nostr-tombolo-filtered-out.csv',
                filteredOutToCsv(finalized.filteredOut, engagement.profiles),
                'text/csv',
              )}
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" aria-hidden />
              Filtered-out list (CSV)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Draw input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Dices className="h-5 w-5 text-primary" aria-hidden />
            Enter your drawn number
          </CardTitle>
          <CardDescription className="text-base">
            Draw a number between <strong className="text-foreground">1 and {maxTicket}</strong> however
            you like — dice, lottery balls, a live stream. The ticket closest to that number wins.
            Exact match wins outright; if two tickets are equally close, the lower ticket number
            wins (shown transparently). The number must be inside the ticket range, otherwise the
            first or last ticket would always win — that wouldn't be fair.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="drawn-number" className="text-base">
                Drawn number (tickets go from 1 to {maxTicket})
              </Label>
              <Input
                id="drawn-number"
                type="number"
                inputMode="numeric"
                min={1}
                max={maxTicket}
                value={drawnInput}
                onChange={(e) => setDrawnInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && inputValid) runDraw(); }}
                placeholder={`1 – ${maxTicket}`}
                className="text-lg h-12"
                aria-invalid={isEntered && !inputValid}
              />
            </div>
            <Button size="lg" className="h-12" onClick={runDraw} disabled={!inputValid}>
              <Crown className="h-4 w-4 mr-2" aria-hidden />
              Find the winner
            </Button>
          </div>
          {isEntered && !inputValid && (
            <p className="text-sm text-destructive" role="alert">
              Please enter a whole number between 1 and {maxTicket}.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {outcome && drawAt && summary && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Crown className="h-6 w-6 text-amber-500" aria-hidden />
              We have a winner!
            </CardTitle>
            <CardDescription className="text-base">
              Drawn number: <strong className="text-foreground text-lg tabular-nums">{outcome.drawnNumber}</strong>
              {' · '}Closest ticket: <strong className="text-foreground text-lg tabular-nums">#{outcome.winner.number}</strong>
              {outcome.winnerDistance === 0 && ' (exact match!)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Winner */}
            <div className="rounded-xl border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-center shrink-0">
                    <p className="text-3xl font-bold tabular-nums">#{outcome.winner.number}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Winner</p>
                  </div>
                  <UserCell
                    pubkey={outcome.winner.participant.pubkey}
                    metadata={engagement.profiles[outcome.winner.participant.pubkey]}
                    size="lg"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    Qualified by: {actionLabel(outcome.winner.participant.actions)}
                  </Badge>
                  {outcome.winnerDistance > 0 && (
                    <Badge variant="outline">{outcome.winnerDistance} away from {outcome.drawnNumber}</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* How the winner was picked — the math, spelled out */}
            <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1">
              <p className="font-medium">How this winner was picked</p>
              <p className="text-muted-foreground">
                Drawn number: <strong className="text-foreground tabular-nums">{outcome.drawnNumber}</strong>.
                Winning ticket <strong className="text-foreground tabular-nums">#{outcome.winner.number}</strong>{' '}
                {outcome.winnerDistance === 0
                  ? 'matches it exactly.'
                  : <>is <strong className="text-foreground tabular-nums">{outcome.winnerDistance}</strong> away (|{outcome.winner.number} − {outcome.drawnNumber}| = {outcome.winnerDistance}) — closer than any other ticket.</>}
              </p>
              <p className="text-muted-foreground">
                Only the distance to the drawn number decides the winner. The trust
                score was used earlier just to decide who gets a ticket — it plays
                no role in the draw itself.
              </p>
            </div>

            {/* Winner tie notice */}
            {outcome.winnerTies.length > 1 && (
              <div className="rounded-lg border border-dashed p-4 text-sm space-y-2">
                <p className="flex items-center gap-2 font-medium">
                  <Scale className="h-4 w-4" aria-hidden />
                  It was a tie — {outcome.winnerTies.length} tickets were equally close
                </p>
                <p className="text-muted-foreground">
                  Tickets {outcome.winnerTies.map((t) => `#${t.number}`).join(' and ')} are both{' '}
                  {outcome.winnerDistance} away from {outcome.drawnNumber}. By the tie-break rule,
                  the lower ticket number (#{outcome.winner.number}) wins.
                </p>
                <ul className="space-y-1">
                  {outcome.winnerTies.map((t) => (
                    <li key={t.participant.pubkey} className="flex items-center gap-2">
                      <span className="font-bold tabular-nums w-10">#{t.number}</span>
                      <UserCell pubkey={t.participant.pubkey} metadata={engagement.profiles[t.participant.pubkey]} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Runner-up */}
            {outcome.runnerUp && (
              <div className="rounded-xl border p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center shrink-0">
                      <p className="text-2xl font-bold tabular-nums">#{outcome.runnerUp.number}</p>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Follow-up</p>
                    </div>
                    <UserCell
                      pubkey={outcome.runnerUp.participant.pubkey}
                      metadata={engagement.profiles[outcome.runnerUp.participant.pubkey]}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">
                      {actionLabel(outcome.runnerUp.participant.actions)}
                    </Badge>
                    <Award className="h-5 w-5 text-muted-foreground" aria-hidden />
                  </div>
                </div>
                {outcome.runnerUpTies.length > 1 && (
                  <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2">
                    <Scale className="h-4 w-4 shrink-0" aria-hidden />
                    Tie for follow-up: tickets {outcome.runnerUpTies.map((t) => `#${t.number}`).join(', ')} were
                    equally close — the lower ticket takes the spot.
                  </p>
                )}
              </div>
            )}

            <Separator />

            {/* Facts */}
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <dt className="text-muted-foreground">Eligible tickets</dt>
                <dd className="font-semibold tabular-nums">{maxTicket}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Notes scanned</dt>
                <dd className="font-semibold tabular-nums">{engagement.notesScanned}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Tickets finalized</dt>
                <dd className="font-semibold">{formatTimestamp(finalized.finalizedAt)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Draw completed</dt>
                <dd className="font-semibold">{formatTimestamp(drawAt)}</dd>
              </div>
            </dl>

            <Separator />

            {/* Exports */}
            <div className="space-y-3">
              <h3 className="font-semibold">Share & export</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => copyText(buildSummaryText(summary), 'Result summary')}>
                  <Copy className="h-4 w-4 mr-2" aria-hidden />
                  Copy result as text
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadTextFile(
                    'nostr-tombolo-report.txt',
                    buildFullReport({
                      summary,
                      noteIds: finalized.noteIds,
                      minScore: finalized.minScore,
                      settings,
                      totals: engagement.totals,
                      tickets: finalized.tickets,
                      filtered: finalized.filteredOut,
                      profiles: engagement.profiles,
                      shuffleSeed: finalized.shuffleSeed,
                    }),
                  )}
                >
                  <Download className="h-4 w-4 mr-2" aria-hidden />
                  Download full raffle report
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadTextFile(
                    'nostr-tombolo-tickets.csv',
                    ticketsToCsv(finalized.tickets, engagement.profiles),
                    'text/csv',
                  )}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" aria-hidden />
                  Ticket list (CSV)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadTextFile(
                    'nostr-tombolo-filtered-out.csv',
                    filteredOutToCsv(finalized.filteredOut, engagement.profiles),
                    'text/csv',
                  )}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" aria-hidden />
                  Filtered-out list (CSV)
                </Button>
              </div>
            </div>

            <Separator />

            <div className="flex justify-center">
              <Button variant="ghost" onClick={resetAll}>
                <RotateCcw className="h-4 w-4 mr-2" aria-hidden />
                Start a new raffle
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
